import { getAvailableDialects } from "@/actions/availableDialects.actions";
import { getCachedParams } from "@/actions/cacheParams.actions";
import {
  getTokenAction,
  getUserAgentAction,
  isTrialEligible,
} from "@/actions/user.actions";
import { apiSdkInstance } from "@/apiSdk/stageApiSdk.instance";
import {
  appRedirectionDeeplink,
  AppRoutesEnum,
  AvoidIntersectingEventsEnum,
  canUserWatchContentOnMobile,
  ContentStatusEnum,
  ContentTypeEnum,
  contentTypeUrlValue,
  CookiesEnum,
  cookiesValues,
  DEFAULT_PAGE_SIZE,
  DialectsEnum,
  EventNamesEnum,
  EventPropertiesEnum,
  FacebookStandardEvents,
  GAEventNamesEnum,
  langDetails,
  LangEnum,
  LocalStorageKeysEnum,
  LoginTypeEnum,
  OSFamilyEnum,
  ParentPostMessageEnum,
  PaymentAppEnum,
  paymentAppValues,
  PlanTypeEnum,
  GamificationPlanIdEnum,
  INTERNATIONAL_PLAN_ID,
  INTERNATIONAL_TRIAL_PLAN_ID,
  remoteConfigDefaultValues,
  RemoteConfigEnum,
  SessionStorageKeysEnum,
  ShowFormatEnum,
  SourcePagesEnum,
  sourcePagesValues,
  SourceWidgetsEnum,
  sourceWidgetValues,
  URLQueryParamsEnum,
  UseTrackingAnalyticsSendToEnum,
  WatchVideoAPIEvents,
  WindowEventListenersEnum,
} from "@/constants/constants";
import { fallbackCopies } from "@/constants/fallbackCopies";
import { logClientError } from "@/lib/datadog-client";
import { logServerError } from "@/lib/datadog-server";
import { reportRumError } from "@/lib/datadog-rum";
import { Thumbnail } from "@/types/common.types";
import {
  MappedContentDetail,
  MappedWatchableItem,
  PostWatchVideoEventsReq,
} from "@/types/consumption.types";
import {
  MappedSelectedPeripheral,
  MappedShowDetail,
  PaginationItem,
  SelectedPeripheral,
} from "@/types/contentDetails.types";
import { TrackEventParams } from "@/types/helper.types";
import { InitPaymentPollingEventParams } from "@/types/payment.types";
import { Dialect } from "@/types/setting.types";
import { TranslationFnType } from "@/types/translations.types";
import {
  UTMDetailsInterface,
  UserDataInterface,
  UserDetailInterface,
} from "@/types/user.types";
import { FocusableComponentLayout } from "@noriginmedia/norigin-spatial-navigation";
import clsx, { ClassValue } from "clsx";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import {
  PHASE_PRODUCTION_BUILD,
  ValueOf,
} from "next/dist/shared/lib/constants";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";
import UAParser from "ua-parser-js";
import { TypeofEnum } from "@/constants/enums";

// Module-level UAParser cache — UA string never changes during a session
interface UACacheData {
  deviceType: string | undefined;
  osName: string | undefined;
  osVersion: string | undefined;
  browserName: string | undefined;
  browserVersion: string | undefined;
}

let uaCache: UACacheData | null = null;

const getUACached = (): UACacheData => {
  if (!uaCache) {
    const parser = new UAParser();
    const device = parser.getDevice();
    const os = parser.getOS();
    const browser = parser.getBrowser();
    uaCache = {
      deviceType: device.type,
      osName: os.name,
      osVersion: os.version,
      browserName: browser.name,
      browserVersion: browser.version,
    };
  }
  return uaCache;
};

export const checkWhetherAgentIsMobile = ({
  userAgent,
}: {
  userAgent: string;
}) => {
  return /mobile/i.test(userAgent);
};

/**
 * @description Get the plan id based on the isTrial and isIndependenceDayOffer flags.
 * ref: https://www.figma.com/board/YMGEHXt1DwGQiOjq60b4o1/Trial-Plan-Flow?node-id=0-1&t=wYitlmV8QHhjkn9Q-1
 * @param isTrial - Whether the user is eligible for trial.
 * @param isIndependenceDayOffer - Whether the user is eligible for independence day offer.
 * @param overridingPlanId - The overriding plan id.
 * @returns The plan id.
 */
export async function getPlanId(
  params: {
    isTrial?: boolean;
    isIndependenceDayOffer?: boolean;
    isDiwaliOffer?: boolean;
    overridingPlanId?: string;
    setCookie?: boolean;
    isInternational?: boolean;
  } | null = null,
) {
  const trialPlanId = "trial-intent-plan-nov",
    subscriptionPlanId = "monthly_199";
  const {
    isTrial,
    isIndependenceDayOffer,
    overridingPlanId,
    isDiwaliOffer,
    setCookie = true,
    isInternational,
  } = params ?? {};

  // International trial users always use hardcoded "trial_stripe".
  // For non-trial international users, use hardcoded "annual_399" only as a
  // fallback for unauthenticated users — logged-in users get plans from user-settings API.
  if (isInternational) {
    if (isTrial) return INTERNATIONAL_TRIAL_PLAN_ID;
    const token = await getToken();
    if (!token) return INTERNATIONAL_PLAN_ID;
  }

  if (!setCookie) {
    return isTrial ? trialPlanId : subscriptionPlanId;
  }

  if (isDiwaliOffer) {
    return "diwali-offer-51-3-399-3";
  }

  const isTrialEligibleUser = isTrial || (await isTrialEligible());
  const planIdKey = isTrialEligibleUser
    ? cookiesValues[CookiesEnum.TrialPlanId]
    : cookiesValues[CookiesEnum.SubscriptionPlanId];

  const isDeeplinkPriorityKey = isTrialEligibleUser
    ? cookiesValues[CookiesEnum.TrialIsDeeplinkPriority]
    : "";

  let planId: string | undefined;
  let isDeeplinkPriority: string | undefined;
  if (isServer()) {
    try {
      const { cookies } = await import("next/headers");
      const cookieStore = await cookies();
      planId = cookieStore?.get(planIdKey)?.value;
      if (isDeeplinkPriorityKey) {
        isDeeplinkPriority = cookieStore?.get(isDeeplinkPriorityKey)?.value;
      }
    } catch (error) {
      // During build time, cookies might not be available
      console.warn("Cookies not available during build:", error);
      planId = "";
      isDeeplinkPriority = "";
    }
  } else {
    planId = getClientSideCookieValue(planIdKey);
    if (isDeeplinkPriorityKey) {
      isDeeplinkPriority = getClientSideCookieValue(isDeeplinkPriorityKey);
    }
  }

  // ref: https://www.figma.com/board/YMGEHXt1DwGQiOjq60b4o1/Trial-Plan-Flow?node-id=0-1&t=wYitlmV8QHhjkn9Q-1
  if (isDeeplinkPriority && overridingPlanId) {
    return overridingPlanId;
  } else if (planId) {
    return planId;
  } else {
    return isTrialEligibleUser ? trialPlanId : subscriptionPlanId;
  }
}

export async function getPlanType() {
  if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD) {
    return PlanTypeEnum.QUARTERLY;
  }

  if (await isTrialEligible()) {
    return PlanTypeEnum.WEEKLY;
  } else {
    return PlanTypeEnum.QUARTERLY;
  }
}

export function getDateAfterDays(days: number) {
  const date = new Date(Date.now());
  date.setDate(date.getDate() + days);
  return date;
}

export function getFormatedDateString(date: Date) {
  const dateString = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
  return dateString;
}

// type hm means return in hm format i.e. 1h 20m instead of 1:20:00 (default)
export function formatTime(seconds: number, type: ":" | "hm" = ":"): string {
  if (type === "hm") {
    const hours: number = Math.floor(seconds / 3600);
    const minutes: number = Math.floor((seconds % 3600) / 60);
    let result: string = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    return result;
  }
  const hours: number = Math.floor(seconds / 3600);
  const minutes: number = Math.floor((seconds % 3600) / 60);
  const remainingSeconds: number = seconds % 60;

  // Zero-padding for minutes and seconds
  const paddedMinutes: string = minutes.toString().padStart(2, "0");
  const paddedSeconds: string = remainingSeconds.toString().padStart(2, "0");

  // Format string based on whether hours are present
  let result: string =
    hours > 0
      ? `${hours}:${paddedMinutes}:${paddedSeconds}`
      : `${minutes}:${paddedSeconds}`;

  return result;
}
export const formDataToObject = (formData: FormData) => {
  const object: any = {};
  formData.forEach((value, key) => {
    try {
      object[key] = value;
    } catch {
      object[key] = value;
    }
  });
  return object;
};

export const generateCryptoBasedUUID = () => {
  const arr = new Uint8Array(16);
  window.crypto.getRandomValues(arr);
  arr[6] = (arr[6] & 0x0f) | 0x40; // Version 4 UUID
  arr[8] = (arr[8] & 0x3f) | 0x80; // Variant 10xx for UUID
  return Array.from(arr)
    .map((b) => ("0" + b.toString(16)).slice(-2))
    .join("");
};

// Generates a unique deeplink for the app: Appends a timestamp as a query parameter to ensure the URL is unique.
// This approach addresses the issue where browsers may not open the deeplink on consecutive clicks if the URL remains unchanged.
export const uniqueAppRedirectionDeeplink = () => {
  if (typeof window === "undefined") {
    return `${appRedirectionDeeplink}?t=${new Date().getTime()}`;
  }
  const contentDeepLink = localStorage.getItem(
    LocalStorageKeysEnum.CONTENT_DEEPLINK,
  );
  if (!contentDeepLink) {
    return `${appRedirectionDeeplink}?t=${new Date().getTime()}`;
  }
  return `${contentDeepLink}?${URLQueryParamsEnum.STITCHING_WEB}=true&t=${new Date().getTime()}`;
};

// Store content deep link in localStorage for app redirection after payment
export const storeContentDeepLink = (deepLink: string | undefined | null) => {
  if (deepLink?.trim()) {
    localStorage.setItem(LocalStorageKeysEnum.CONTENT_DEEPLINK, deepLink);
  }
};

// payment fail redirection
export const paymentFailedRedirection = async (
  redirected: boolean,
  trackEvent: ({}: TrackEventParams) => Promise<void>,
  eventProperties: Record<string, string>,
  setRedirectedState: ((state: boolean) => void) | null,
  router: AppRouterInstance,
  redirectTo: string | null,
  showPaymentFailPage: boolean = true,
) => {
  if (!redirected) {
    await trackEvent({
      eventName: EventNamesEnum.PAYMENT_FAILED,
      properties: {
        ...eventProperties,
        payment_method: "upi",
      },
      sendTo: [],
    });

    if (setRedirectedState) {
      setRedirectedState(true);
    }

    const searchParams = new URLSearchParams(window.location.search);
    const directlyFromLogin = searchParams.get(
      URLQueryParamsEnum.DIRECLTY_FROM_LOGIN,
    );

    let queryString = redirectTo
      ? `?redirectionAfterPaymentTo=${redirectTo}`
      : "";
    if (directlyFromLogin) {
      const direcltyFromLoginQueryString = `${URLQueryParamsEnum.DIRECLTY_FROM_LOGIN}=true`;
      if (queryString.includes("?")) {
        queryString += `&${direcltyFromLoginQueryString}`;
      } else {
        queryString = `?${direcltyFromLoginQueryString}`;
      }
    }

    const isCheckoutPage =
      showPaymentFailPage && window.location.pathname.includes("/checkout");

    if (isCheckoutPage) {
      router.replace(`/payment-fail${queryString}`);
    }
  }
};

// payment success redirection
export const paymentSuccessRedirection = async (
  redirected: boolean,
  setRedirectedState: ((state: boolean) => void) | null,
  router: AppRouterInstance,
  trackEvent: ({}: TrackEventParams) => Promise<void>,
  redirectTo: string | null,
) => {
  if (!redirected) {
    await trackEvent({
      eventName: EventNamesEnum.START_TRIAL,
      properties: {
        value: 1,
        currency: "INR",
        ga_client_id: await getUserClientId(),
      },
      sendTo: [
        UseTrackingAnalyticsSendToEnum.FIREBASE,
        UseTrackingAnalyticsSendToEnum.FACEBOOK,
      ],
    });
    if (setRedirectedState) {
      setRedirectedState(true);
    }

    const searchParams = new URLSearchParams(window.location.search);
    const directlyFromLogin = searchParams.get(
      URLQueryParamsEnum.DIRECLTY_FROM_LOGIN,
    );

    let queryString = redirectTo
      ? `?redirectionAfterPaymentTo=${redirectTo}`
      : "";
    if (directlyFromLogin) {
      const direcltyFromLoginQueryString = `${URLQueryParamsEnum.DIRECLTY_FROM_LOGIN}=true`;
      if (queryString.includes("?")) {
        queryString += `&${direcltyFromLoginQueryString}`;
      } else {
        queryString = `?${direcltyFromLoginQueryString}`;
      }
    }
    const isCheckoutPage = window.location.pathname.includes("/checkout");
    const successPageUrl = `/payment-success${queryString}`;
    if (isCheckoutPage) {
      router.replace(successPageUrl);
    } else {
      router.push(successPageUrl);
    }
  }
};

export const getClientSideCookieValue = (name: string): string | undefined => {
  if ("undefined" === typeof document) return undefined;
  const cookieValue = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];

  return cookieValue;
};

/**
 * Set a cookie value on the client side
 * @param name - The name of the cookie
 * @param value - The value of the cookie
 * @param options - Optional cookie settings
 * @param options.daysToExpire - The number of days to expire the cookie
 * @param options.path - The path of the cookie
 * @param options.secure - Whether the cookie is secure, only supported in https
 * @param options.sameSite - The same site policy for the cookie
 */
export const setClientSideCookieValue = (
  name: string,
  value: string,
  options: {
    daysToExpire?: number;
    path?: string;
    secure?: boolean;
    sameSite?: "Strict" | "Lax" | "None";
  } = {},
): void => {
  if (TypeofEnum.UNDEFINED === typeof document) return;

  const {
    daysToExpire = undefined,
    path = "/",
    secure = false,
    sameSite = "Lax",
  } = options;

  let cookieStr = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=${path}; SameSite=${sameSite}`;

  // daysToExpire is only supported in client side
  if (daysToExpire) {
    const date = new Date();
    if (TypeofEnum.NUMBER === typeof daysToExpire) {
      date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
    }
    cookieStr += `; Expires=${date.toUTCString()}`;
  }

  // secure is only supported in https
  if (secure) {
    cookieStr += "; Secure";
  }

  document.cookie = cookieStr;
};

/**
 * Delete a cookie value on the client side
 * @param name - The name of the cookie
 */
export const deleteClientSideCookieValue = (name: string) => {
  if (TypeofEnum.UNDEFINED === typeof document) return;
  document.cookie = `${name}=; Path=/; SameSite=Lax; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

/**
 * Get a cookie value on the server side
 * @param name - The name of the cookie
 */
export const getServerSideCookieValue = async (
  name: string,
): Promise<string | undefined> => {
  if (!isServer()) return undefined;
  const { cookies } = await import("next/headers");
  const cookieStore = await cookies();
  return cookieStore.get(name)?.value;
};

/**
 * Set a cookie value on the server side
 * @param name - The name of the cookie
 * @param value - The value of the cookie
 * @param options - Optional cookie settings
 * @param options.daysToExpire - The number of days to expire the cookie
 * @param options.secure - Whether the cookie is secure
 * @param options.sameSite - The same site policy for the cookie
 */
export const setServerSideCookieValue = async (
  name: string,
  value: string,
  options?: {
    daysToExpire?: number;
    secure?: boolean;
    sameSite?: "Strict" | "Lax" | "None";
  },
) => {
  if (!isServer()) return;
  const { cookies } = await import("next/headers");
  const cookieStore = await cookies();
  cookieStore.set(name, value, {
    maxAge: options?.daysToExpire,
    secure: options?.secure,
    sameSite: options?.sameSite?.toLowerCase() as
      | "lax"
      | "strict"
      | "none"
      | undefined,
  });
};

/**
 * Delete a cookie value on the server side
 * @param name - The name of the cookie
 */
export const deleteServerSideCookieValue = async (name: string) => {
  if (!isServer()) return;
  const { cookies } = await import("next/headers");
  const cookieStore = await cookies();
  cookieStore.delete(name);
};

/**
 * Checks if a content is in the free watch flow based on the FreeWatchFlow cookie.
 *
 * LIFECYCLE & TRUST MODEL:
 * - The FreeWatchFlow cookie is set by the client-side Title component using the
 *   setCookieViaAction server action when a user views a title page trailer.
 * - The cookie stores the contentId (titleId) of the content being previewed.
 * - This helper is used in server components (generateMetadata, page components)
 *   to determine if the current content matches the free watch flow cookie value.
 * - The cookie enables users to watch content without login when they come from
 *   the title page preview flow.
 *
 * @param cookieStore - The Next.js cookies() store (from next/headers)
 * @param titleId - The content ID to check against the cookie value
 * @returns true if the cookie value matches the titleId, false otherwise
 *
 * @example
 * ```ts
 * const cookieStore = await cookies();
 * const isFreeWatchFlow = checkIsFreeWatchFlow(cookieStore, parsedTitleId);
 * ```
 */
export function checkIsFreeWatchFlow(
  cookieStore: Awaited<ReturnType<typeof import("next/headers").cookies>>,
  titleId: string | number,
): boolean {
  const freeWatchFlowContentId = cookieStore.get(
    cookiesValues[CookiesEnum.FreeWatchFlow],
  );
  return freeWatchFlowContentId?.value === String(titleId);
}

export const isServer = () => typeof window === "undefined";

export const getDefaultLang = () => {
  return process.env.NEXT_PUBLIC_DEFAULT_LANG || "en";
};

export const getDefaultDialect = () => {
  return process.env.NEXT_PUBLIC_DEFAULT_DIALECT || DialectsEnum.HARYANVI;
};

export const getCurrentLang = async (): Promise<string> => {
  // TODO fix this: required for e2e testing
  if (!process.env.NODE_ENV) {
    return getDefaultLang();
  }
  try {
    if (isServer()) {
      const cachedParams = await getCachedParams();
      return cachedParams.lang || getDefaultLang();
    } else {
      const clientLang = getClientSideCookieValue(
        cookiesValues[CookiesEnum.Lang],
      );
      return clientLang || getDefaultLang();
    }
  } catch (error) {
    captureError("Error getting language", error);
    return getDefaultLang();
  }
};

//  This function takes a string and replaces every occurrence of %d, %p, or %m with the corresponding index in the values array.
export const interpolateStringValues = (
  str: string,
  values?: (string | number)[],
) => {
  if (!values || values.length === 0) {
    return str;
  }

  let valueIndex = 0;
  return str.replace(/%[dpmk]/g, (match) => {
    if (valueIndex < values.length) {
      const value = values[valueIndex++];
      return match === "%d" ? String(value) : String(value);
    }
    return match;
  });
};

/**
 * This function creates a translation function from the provided strings and language.
 * The translation function takes a key and returns the corresponding translation string.
 *
 * @param strings - The object containing translation strings.
 * @param lang - The language code for the translations.
 * @returns A function that takes a key and optional values, and returns the corresponding translation string.
 *
 * @example
 * const translate = createTranslationFunction(stringsJson, 'en');
 * console.log(translate('greeting.morning', ['John'])); // Output: "Good morning, John!"
 */
export function createTranslationFunction(
  strings: Record<string, any>,
  lang: string,
): TranslationFnType {
  return (key: string, values?: (string | number)[]) => {
    const keyParts = key.split(".");
    let result: any = strings;

    for (const keyPart of keyParts) {
      if (result && typeof result === "object" && keyPart in result) {
        result = result[keyPart];
      } else {
        result = undefined;
        break;
      }
    }

    if (typeof result === "string") {
      return interpolateStringValues(result, values);
    }

    // search for local json if strings not found in api
    if (lang in fallbackCopies) {
      let fallbackResult: any =
        fallbackCopies[lang as keyof typeof fallbackCopies];

      for (const keyPart of keyParts) {
        if (
          fallbackResult &&
          typeof fallbackResult === "object" &&
          keyPart in fallbackResult
        ) {
          fallbackResult = fallbackResult[keyPart];
        } else {
          fallbackResult = undefined;
          break;
        }
      }

      if (typeof fallbackResult === "string") {
        return interpolateStringValues(fallbackResult, values);
      }
    }
    return key;
  };
}

export const getAvailableLangCodes = () => {
  return Object.values(LangEnum);
};

export const isStringHindi = (title: string): boolean => {
  const hindiRegex = /[\u0900-\u097F]/; // for this regex refer https://stackoverflow.com/questions/41356013/how-to-detect-if-a-string-contains-hindi-devnagri-in-it-with-character-and-wor
  return hindiRegex.test(title);
};

// this fn converts showname to url slug
export const generateURLSlug = ({
  contentName,
  fallbackSlug,
  contentId,
}: {
  contentName: string;
  fallbackSlug?: string;
  contentId: number;
}) => {
  let slug = "";
  // if showname is hindi use slug from api
  if (isStringHindi(contentName) && fallbackSlug) {
    slug = fallbackSlug.trim().replace(/-+/g, "-"); // Replace multiple hyphens with a single hyphen
  } else {
    slug = contentName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "") // Remove all special characters except spaces
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-"); // Replace multiple hyphens with a single hyphen
  }

  return slug !== "" ? `${slug}-${contentId}` : `${contentId}`;
};

export const getIdFromSlug = (slug: string) => {
  return slug.split("-").pop() || "";
};

export const getSiteBaseURL = () => {
  return process.env.NEXT_PUBLIC_SITE_URL || "https://www.stage.in";
};

export const getSiteMapLastMod = () => {
  const date = new Date();

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");

  const formattedDate = `${year}-${month}-${day}`; // refer https://developers.google.com/search/blog/2006/04/using-lastmod-attribute for format

  return formattedDate;
};

export async function fetchConfigAsNumber(
  key: RemoteConfigEnum,
): Promise<number> {
  const defaultValue = remoteConfigDefaultValues[key] as number;
  if (process.env.NODE_ENV === "test") {
    return defaultValue;
  }

  if (typeof window === "undefined") return defaultValue;

  // Dynamic import to avoid bundling Firebase in Edge Runtime (middleware)
  const { fetchAndActivate, getRemoteConfig, getValue } = await import(
    "firebase/remote-config"
  );
  const { getApps, initializeApp } = await import("firebase/app");
  const { firebaseConfig } = await import("@/utils/firebaseConfig");

  const app =
    getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
  const remoteConfig = getRemoteConfig(app);
  remoteConfig.defaultConfig = remoteConfigDefaultValues;
  await fetchAndActivate(remoteConfig);

  const configValue = getValue(remoteConfig, key).asNumber();

  return configValue;
}

export const pollForTruecallerSuccess = async ({
  uniqueRequestId,
  changeTruecallerLoginSuccess,
}: {
  uniqueRequestId: string;
  changeTruecallerLoginSuccess: Function;
}) => {
  const maxPollRequests = 60;
  let pollCount = 0;

  const pollInterval = setInterval(async () => {
    try {
      const { verifiedProfileStatus } =
        await apiSdkInstance.user.fetchTruecallerProfileStatus({
          uniqueRequestId,
        });

      switch (verifiedProfileStatus) {
        case "PENDING":
          pollCount++;
          if (pollCount >= maxPollRequests) {
            changeTruecallerLoginSuccess(false);
            clearInterval(pollInterval);
            return;
          }
          break;

        case "FAILURE":
        case "REJECTED":
          changeTruecallerLoginSuccess(false);
          clearInterval(pollInterval);
          return;

        case "SUCCESS":
          changeTruecallerLoginSuccess(true);
          clearInterval(pollInterval);
          return;

        default:
          clearInterval(pollInterval);
          return;
      }
    } catch (error) {
      changeTruecallerLoginSuccess(false);
      clearInterval(pollInterval);
      // alert("Error fetching Truecaller profile status")
      console.error("Error fetching Truecaller profile status:", error);
      return;
    }
  }, 1000);

  return pollInterval;
};

const nonReportableErrors = [
  // not to capture redirect errors that are thrown by nextjs to handle server side redirects
  "NEXT_REDIRECT",
  "NOT_FOUND",
  "Maybe the media is not available",
];

export const captureError = (error: unknown, params?: any) => {
  const shouldIgnoreReporting = nonReportableErrors.some(
    (message) => (error as Error)?.message?.includes(message) || false,
  );
  if (shouldIgnoreReporting) return error;

  if (!process.env.NODE_ENV) return error;

  // Report to RUM on client-side
  if (!isServer()) {
    reportRumError(error, params);
  }

  (async () => {
    const finalParams = { ...(params || {}) };

    if (isServer()) {
      logServerError(error, finalParams as Record<string, any>);
    } else {
      logClientError(error, finalParams);
    }
  })();

  return error;
};

export const getContentTypeUrlValue = (
  contentType: string,
  format?: ShowFormatEnum,
): ValueOf<typeof contentTypeUrlValue> => {
  if (format === ShowFormatEnum.MICRO_DRAMA) {
    return contentTypeUrlValue[ShowFormatEnum.MICRO_DRAMA];
  }
  const _contentType =
    contentType === ContentTypeEnum.SHOW
      ? ContentTypeEnum.SHOW
      : ContentTypeEnum.MOVIE;
  return contentTypeUrlValue[_contentType];
};

export const generateContentDetailURL = (
  lang: LangEnum,
  dialect: string,
  contentType: ValueOf<typeof contentTypeUrlValue>,
  slug?: string,
  queryParams?: Record<string, string | number | boolean>,
) => {
  const url = `/${lang}/${dialect}/${contentType}${slug ? "/" + slug : ""}`;

  return queryParams ? addQueryParamsToURL(url, queryParams) : url;
};

export const generateDetailedRowURL = (
  lang: LangEnum,
  dialect: string,
  rowKey: string,
) => {
  if (!dialect) {
    return `/${lang}/detail/${rowKey}`;
  }
  return `/${lang}/${dialect}/detail/${rowKey}`;
};

export const generateTrailerPageURL = (
  lang: LangEnum,
  dialect: string,
  contentType: string,
  slug?: string,
  seasonId?: number | null,
) => {
  return `/${lang}/${dialect}/${contentType}/trailer${slug ? "/" + slug : ""}${seasonId ? "?seasonId=" + seasonId : ""}`;
};

export const generateHomeURL = (lang: string, dialect: string) =>
  `/${lang}/${dialect}`;

export const generateCharacterChatURL = ({
  lang,
  dialect,
  contentType,
  contentSlug,
  characterSlug,
}: {
  lang: string;
  dialect: string;
  contentType: string;
  contentSlug: string;
  characterSlug: string;
}) =>
  `/${lang}/${dialect}/${contentType}/${contentSlug}${AppRoutesEnum.CHARACTER_CHAT}?${URLQueryParamsEnum.CHARACTER}=${encodeURIComponent(characterSlug)}`;

export const generateCharacterListURL = ({
  lang,
  dialect,
  contentType,
  contentSlug,
  accessToken,
}: {
  lang: string;
  dialect: string;
  contentType: string;
  contentSlug: string;
  accessToken?: string;
}) =>
  `/${lang}/${dialect}/${contentType}/${contentSlug}/characters${accessToken ? `?${URLQueryParamsEnum.ACCESS_TOKEN}=${accessToken}` : ""}`;

export const generateGenreDetailUrl = ({
  lang,
  dialect,
  slug,
  id,
}: {
  lang: string;
  dialect: string;
  slug: string;
  id?: number;
}) => {
  //If the id parameter is not provided, the slug should include the id in its values
  return id
    ? `/${lang}/${dialect}/genre/${slug}-${id}`
    : `/${lang}/${dialect}/genre/${slug}`;
};

export const generateWatchPageURL = (
  lang: LangEnum,
  dialect: string,
  contentType: string,
  slug?: string,
  episodeNo?: number | null,
  includeCurrQueryParams = true,
  additionalQueryParams?: { [key: string]: string | undefined },
  format?: ShowFormatEnum,
) => {
  let url = `/${lang}/${dialect}/${contentType}${contentType != contentTypeUrlValue[ShowFormatEnum.MICRO_DRAMA] ? "/watch" : ""}${slug ? "/" + slug : ""}${episodeNo ? "/" + episodeNo : ""}`;

  if (!isServer() && includeCurrQueryParams) {
    const currenturl = new URL(window.location.href);
    const searchParams = new URLSearchParams(currenturl.search);
    if (additionalQueryParams) {
      // This ensures that when including current query parameters, any additional query parameters will overwrite existing ones with the same key in the URL.
      for (const [key, value] of Object.entries(additionalQueryParams)) {
        if (value !== undefined) {
          searchParams.set(key, value);
        }
      }
    }

    const queryString = searchParams.toString();
    if (queryString) {
      url += "?" + queryString;
    }
    return url;
  } else if (additionalQueryParams) {
    const queryParams = Object.entries(additionalQueryParams)
      .filter(([_, value]) => value !== undefined)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value!)}`,
      )
      .join("&");

    if (queryParams) {
      url += url.includes("?") ? "&" : "?";
      url += queryParams;
    }
  }
  return url;
};

export const getLangDetailsFromApiKey = (
  apiKey: string,
): { langCode: LangEnum; apiKey: string; label: string } | undefined => {
  const langEntry = Object.values(langDetails).find(
    (lang) => lang.apiKey === apiKey,
  );
  if (langEntry) {
    const langCode = Object.keys(langDetails).find(
      (key) => langDetails[key as LangEnum].apiKey === apiKey,
    ) as LangEnum;
    return { langCode, ...langEntry };
  }
  return undefined;
};

export const getPlayerEventProperties = ({
  content,
  subtitleLanguage = "off",
  playbackQuality = "auto",
  playbackSpeed = 1,
  videoPositionSeconds = 0,
  userId = "",
  properties = {},
  actionSource = null,
}: {
  content: MappedWatchableItem;
  subtitleLanguage: string;
  playbackQuality: string;
  playbackSpeed: number;
  videoPositionSeconds?: number;
  userId?: string;
  properties?: Object;
  actionSource?: string | null;
}) => {
  const ua = getUACached();
  const devicePlatform = ua.deviceType;
  const deviceOs = ua.osName;
  const deviceLanguage = navigator.language;
  const websiteDisplayLanguage = content.displayLanguage;

  return {
    action_source: actionSource,
    content_id: content._id,
    content_name: content.title,
    content_type:
      "episodeOrder" in content ? ContentTypeEnum.SHOW : ContentTypeEnum.MOVIE,
    content_dialect: content.language,
    // this field is not present in DB, so sending null
    content_dialect_original: null,
    player_type: "vidstack",
    player_mode: screen.orientation.type.includes("portrait")
      ? "portrait"
      : "landscape",
    connection_mode: "online",
    subtitile_language: subtitleLanguage,
    playback_quality: playbackQuality,
    playback_speed: playbackSpeed,
    video_position_seconds: videoPositionSeconds,
    user_id: userId,
    device_platform: devicePlatform,
    device_os: deviceOs,
    device_language: deviceLanguage,
    website_display_language: websiteDisplayLanguage,
    ...properties,
  };
};

export const addQueryParamsToURL = (
  url: string,
  queryParams: Record<string, string | number | boolean>,
) => {
  const queryString = Object.entries(queryParams)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join("&");

  return queryString
    ? `${url}${url.includes("?") ? "&" : "?"}${queryString}`
    : url;
};

export const getContentDetail = async ({
  contentType,
  titleId,
  lang,
  dialect,
  seasonId,
  addToken,
  pagination,
}: {
  titleId: number;
  dialect: string;
  lang: string;
  contentType: string;
  seasonId?: string | null;
  addToken?: boolean;
  pagination?: PaginationItem;
}) => {
  let contentDetail: MappedContentDetail;

  if (contentType === contentTypeUrlValue[ContentTypeEnum.MOVIE]) {
    contentDetail = await apiSdkInstance.consumption.getMovieDetails({
      movieId: titleId,
      dialect: dialect,
      lang: lang,
      addToken,
    });
  } else {
    contentDetail = await apiSdkInstance.consumption.getShowDetails({
      titleId: titleId,
      dialect: dialect,
      lang: lang,
      seasonId,
      addToken,
      pagination,
    });
  }
  return contentDetail;
};

export const diffInDatesInHours = (fromDate: Date, toDate: Date) => {
  const diffInMilliseconds = toDate.getTime() - fromDate.getTime();

  return diffInMilliseconds / (1000 * 60 * 60);
};

export function getRandomDialect(): DialectsEnum {
  const dialects = Object.values(DialectsEnum);
  const randomIndex = Math.floor(Math.random() * dialects.length);
  return dialects[randomIndex];
}

export function generateWatchEventsAPIPayload({
  consumdedDuration,
  contentId,
  contentType,
  eventType,
  totalDuration,
  additionalData,
}: {
  consumdedDuration: number;
  contentId: number;
  contentType: string;
  eventType: WatchVideoAPIEvents;
  totalDuration: number;
  additionalData?: {};
}): PostWatchVideoEventsReq {
  return {
    consumedDuration: consumdedDuration,
    contentID: contentId,
    contentType: contentType,
    data: additionalData,
    eventType: eventType,
    timestamp: getCurrEpochTime(),
    totalDuration: totalDuration,
  };
}

export const getCurrEpochTime = () => {
  return Date.now();
};

export const getUserAgent = async () => {
  if (isServer()) {
    try {
      return await getUserAgentAction();
    } catch (error) {
      // If headers() is called outside request scope, return empty string
      // This can happen during build time or static generation
      return "";
    }
  }
  return navigator.userAgent;
};

export const getOsName = async () => {
  try {
    if (!isServer()) {
      return getUACached().osName || OSFamilyEnum.OTHER;
    }
    const userAgent = await getUserAgent();
    if (!userAgent) {
      return OSFamilyEnum.OTHER; // Default fallback
    }
    const parser = new UAParser(userAgent);
    return parser.getOS().name || OSFamilyEnum.OTHER;
  } catch (error) {
    return OSFamilyEnum.OTHER; // Default fallback
  }
};

export const getOsFamily = async () => {
  try {
    const osName = await getOsName();
    if (osName.toLowerCase().includes("android")) {
      return OSFamilyEnum.ANDROID;
    } else if (osName.toLowerCase().includes("ios")) {
      return OSFamilyEnum.iOS;
    }
    // returning other as default os because different os are not supported by the api
    return OSFamilyEnum.OTHER;
  } catch (e) {
    captureError("Error in getOsFamily", e);
    return OSFamilyEnum.OTHER;
  }
};

export const updateQueryparam = (
  pathUrl: string,
  queryParamName: string,
  newValue: string,
) => {
  // Split URL into base and query parts
  const [basePath, queryString] = pathUrl.split("?");
  const queryParams = new URLSearchParams(queryString || "");
  queryParams.set(queryParamName, newValue);

  return `${basePath}?${queryParams.toString()}`;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getThumbnailIdFromURL(thumbnailURL: string) {
  if (thumbnailURL.includes("/")) {
    return thumbnailURL.split("/").pop() || "";
  }
  return thumbnailURL;
}

export function isE2ETestEnv() {
  const isPlaywrightEnv = !process.env.NODE_ENV;
  const isCypressEnv =
    typeof Cypress !== "undefined" && Cypress.env("IS_CYPRESS_ENV");

  return isPlaywrightEnv || isCypressEnv;
}

/**
 * Calculates the number of items that can fit in one row within a container.
 *
 * @param itemWidth - The width of each item (must be greater than zero).
 * @param padding - The spacing between items in the row.
 * @param isDesktop - Indicates if the layout is for a desktop device.
 * @param containerWidth - The width of the container (defaults to `window.innerWidth`).
 * @returns Number of items that can fit in one row or `DEFAULT_PAGE_SIZE` if conditions are not met.
 */
export const calculateItemsPerRow = (
  itemWidth: number,
  padding: number,
  isDesktop: boolean,
  containerWidth = window.innerWidth,
): number => {
  if (!itemWidth || !isDesktop) {
    return DEFAULT_PAGE_SIZE;
  }

  const itemTotalWidth = itemWidth + padding;

  return Math.round(containerWidth / itemTotalWidth);
};

export const getDialectLabelFromDialectObj = ({
  dialect,
  language,
}: {
  dialect: Dialect;
  language: LangEnum;
}) => {
  const langApiKey = langDetails[language].apiKey;
  const dialectLabelKey =
    "dialect" + langApiKey[0].toUpperCase() + langApiKey.slice(1);

  return dialect[dialectLabelKey as keyof Dialect] as string;
};

export const getDialectObjFromDialectUrlValue = (
  dialect: string,
  availableDialects: Dialect[],
) => {
  return availableDialects.find((_dialect) => _dialect.urlValue === dialect);
};

export const getDialectLabelFromURLDialect = async (
  dialect: string,
  lang: string,
) => {
  const availableDialect = await getAvailableDialects();
  const dialectObj = getDialectObjFromDialectUrlValue(
    dialect,
    availableDialect,
  );

  if (!dialectObj) return getDefaultDialect();

  const dialectLabel = getDialectLabelFromDialectObj({
    dialect: dialectObj,
    language: lang as LangEnum,
  });

  return dialectLabel;
};

/**
 * Generates URLs for different types of thumbnails (horizontal, vertical, square) from the given `Thumbnail` object.
 *
 * @param {Thumbnail} thumbnail - The `Thumbnail` object containing the source links for different thumbnail types.
 * @param {boolean} [isShow=true] - Flag to determine whether the thumbnail is for a "show" or a "movie". Defaults to true ("show").
 * @param {string} [thumbnailSize="small"] - The size of the thumbnail. Can be "large", "medium", or "small". Defaults to "small".
 * @param {string} [horizontalThumbnailRatio="ratio1"] - The horizontal ratio for the horizontal thumbnail. Can be "ratio1" or "ratio3". Defaults to "ratio1".
 *
 *
 * @returns - An object containing the URLs for horizontal, vertical, and square thumbnails.
 *                     Each URL is constructed using the provided `thumbnail` object and the environmental CDN root URL.
 * **/
export const getThumbnailsFromObject = (
  thumbnail: Thumbnail,
  isShow: boolean = true,
  thumbnailSize: "large" | "small" | "medium" = "small",
  horizontalThumbnailRatio: "ratio1" | "ratio3" = "ratio1",
) => {
  return {
    horizontalThumbnail: `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/${isShow ? "show" : "episode"}/horizontal/${thumbnailSize}/${thumbnail?.horizontal?.[horizontalThumbnailRatio]?.sourceLink || ""}`,
    verticalThumbnail: `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/${isShow ? "show" : "episode"}/vertical/${thumbnailSize}/${thumbnail?.vertical?.ratio1.sourceLink || ""}`,
    squareThumbnail: `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/${isShow ? "show" : "episode"}/square/${thumbnailSize}/${thumbnail?.square?.ratio1?.sourceLink || ""}`,
    gradientColor: thumbnail?.horizontal?.ratio1?.gradient,
  };
};

export function getPaymentAppValue(key: string): PaymentAppEnum {
  // Check if the key exists in paymentAppValues; if not, return PaymentAppEnum.OTHER
  return paymentAppValues[key] || PaymentAppEnum.OTHER;
}

export async function createMandate(
  eventProperties: Record<string, string>,
  overridingPlanId: string | undefined,
  paymentApp: PaymentAppEnum,
  paymentGateway: string,
) {
  const upiIntentLink = await apiSdkInstance.payment.createMandate({
    eventProperties,
    paymentApp,
    overridingPlanId,
    paymentGateway,
  });

  return upiIntentLink;
}

export function initiateGlobalPaymentPolling(
  isNewPaymentFlow = false,
  showPaymentFailPage = true,
) {
  const event = new CustomEvent<InitPaymentPollingEventParams>(
    WindowEventListenersEnum.INITPAYMENTPOLLING,
    {
      detail: {
        isNewPaymentFlow,
        showPaymentFailPage,
      },
    },
  );
  window.dispatchEvent(event);
}

export function stopGlobalPaymentPolling() {
  const event = new CustomEvent(WindowEventListenersEnum.STOPPAYMENTPOLLING);
  window.dispatchEvent(event);
}

export const getTrailerURLsFromSelectedPeripheral = ({
  selectedPeripheral,
  contentType,
  slug,
}: {
  selectedPeripheral: SelectedPeripheral;
  contentType: ContentTypeEnum;
  slug: string;
}): MappedSelectedPeripheral => {
  return {
    playbackUrl: selectedPeripheral?.sourceLink
      ? `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/${contentType === ContentTypeEnum.SHOW ? "show" : "episode"}/main-video/480/${selectedPeripheral?.sourceLink}`
      : null,
    hlsPlaybackUrl: selectedPeripheral?.visionularHls?.hlsSourcelink
      ? `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/trailers/${contentType}/${slug}/trailer/HLS/${selectedPeripheral?.visionularHls?.hlsSourcelink}`
      : null,

    hlsH265PlaybackUrl: selectedPeripheral?.visionularHls?.hlsSourcelink
      ? `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/trailers/${contentType}/${slug}/trailer/HLS-H265/${selectedPeripheral?.visionularHlsH265?.hlsSourcelink}`
      : null,
  };
};

export const showMandatePausedToast = (
  e: MouseEvent | null,
  t: TranslationFnType,
) => {
  if (e) {
    e.preventDefault();
  }

  const mandatePausedMessage = t("content_detail.mandate_paused_toast");
  if (!toast.isActive(mandatePausedMessage)) {
    toast.error(mandatePausedMessage, {
      toastId: mandatePausedMessage, // Use message as ID to prevent duplicates
    });
  }
};

/**
 * Determines the appropriate click handler for watch content related actions for subscribed users on Content Detail page.
 *
 * @param startWatchingRedirectionUrl - (if redirectionUrl query param present) Deep link URL for app redirection, null if not applicable
 * @param handleDeepLinkURL - (if redirectionUrl query param present) Function to handle app deep linking
 * @param isMobile - Whether the user is on a mobile device
 * @param openAppDownloadPopup - Function to show app download popup
 * @param isMandatePaused - Whether the user's mandate is paused
 * @param t - Translation function
 * @returns Click handler function or undefined
 */
export const getWatchClickHandler = (
  startWatchingRedirectionUrl: string | null,
  handleDeepLinkURL: () => void,
  isMobile: boolean,
  openAppDownloadPopup: (e?: any) => void,
  isMandatePaused: boolean,
  t: (key: string) => string,
) => {
  if (startWatchingRedirectionUrl) {
    return handleDeepLinkURL;
  } else if (isMobile && !canUserWatchContentOnMobile) {
    return openAppDownloadPopup;
  } else if (isMandatePaused) {
    return (e: any) => showMandatePausedToast(e, t);
  }
  return undefined;
};

/**
 * Ensures a number stays between a minimum and maximum value.
 * If the number is less than the minimum, it returns the minimum.
 * If it's more than the maximum, it returns the maximum.
 * Otherwise, it returns the number itself.
 */
export const numberWithinRange = (
  number: number,
  min: number,
  max: number,
): number => Math.min(Math.max(number, min), max);

// isTVEnv is used to check if the user is on a TV device
// useCase:
// 1. show logo in tv
// 2. show loader along with logo when fb pixel is loading instead of just black screen
// 3. use hardcoded height and width as aspect ratio css is not supported in tv
// 4. use hardcoded margin of flex elements as flex gap is not supported in tv
// 5. change platter thumbnail ratio for tv
export const isTvEnv = () => {
  return process.env.NEXT_PUBLIC_IS_TV === "true";
};

// this is used by tv app to check if the modal is open.
export const setIsModalOnWindow = (isModalOpen: boolean, key?: string) => {
  if (isModalOpen) {
    window.isModalOpen = true;
    window.isModalOpenKey = key || null;
  } else if (!key || key === window.isModalOpenKey) {
    window.isModalOpen = false;
    window.isModalOpenKey = null;
  }
};

// this is used by tv app to close the modal,
// If a modal is open (window.isModalOpen is true), close it when Back is pressed instead of navigating back
export const setCloseModalOnWindow = (fn: () => void) => {
  window.closeModal = fn;
};

export const clearCloseModalOnWindow = (key?: string) => {
  if (!key || window.isModalOpenKey === key) {
    window.closeModal = undefined;
  }
};

export const getPortraitTizenTVItemDimensionClasses = () => {
  return isTvEnv() ? "aspect-2-3-fallback" : "";
};

export const postLoginEvents = async (
  userDetail: UserDetailInterface,
  loginType: LoginTypeEnum,
  sourcePage: string,
  trackEvent: (event: TrackEventParams) => Promise<void>,
  identifyUser: (parsedUserObj: UserDataInterface) => void,
) => {
  identifyUser({
    userId: userDetail?._id,
    mobileNumber: userDetail?.primaryMobileNumber,
  });
  await trackEvent({
    eventName: EventNamesEnum.LOGIN_SUCCESSFUL,
    properties: {
      login_method:
        loginType === LoginTypeEnum.PHONE_NUMBER
          ? "mobile_otp"
          : loginType === LoginTypeEnum.QR_CODE
            ? "login_qr_code"
            : "truecaller",
      source_page_name: sourcePage,
      mobileNumber: userDetail.primaryMobileNumber,
    },
    gaOverRide: {
      eventName: GAEventNamesEnum.LOGIN,
    },
    sendTo: [UseTrackingAnalyticsSendToEnum.FACEBOOK],
  });

  updateUserClientId();

  if (userDetail?.isNewUser) {
    await trackEvent({
      eventName: EventNamesEnum.SIGNUP_SUCCESSFUL,
      properties: {
        registration_method: loginType,
        method: loginType,
      },
      fbOverRide:
        loginType === LoginTypeEnum.PHONE_NUMBER
          ? {
              eventName: FacebookStandardEvents.COMPLETE_REGISTRATION,
            }
          : undefined,
      sendTo: [
        UseTrackingAnalyticsSendToEnum.FIREBASE,
        ...(loginType === LoginTypeEnum.PHONE_NUMBER
          ? [UseTrackingAnalyticsSendToEnum.FACEBOOK]
          : []),
      ],
      gaOverRide:
        loginType === LoginTypeEnum.PHONE_NUMBER
          ? {
              eventName: GAEventNamesEnum.SIGN_UP,
            }
          : undefined,
    });
  }

  if (!userDetail.initialUserCulture) {
    const userCulture = getClientSideCookieValue(
      cookiesValues[CookiesEnum.UserCulture],
    );
    if (userCulture) {
      await trackEvent({
        eventName: EventNamesEnum.USER_CULTURE_RECEIVED,
        properties: {
          culture_received: userCulture,
          page_name: localStorage.getItem(
            LocalStorageKeysEnum.USER_CULTURE_SELECTION_SOURCE,
          ),
        },
        sendTo: [
          UseTrackingAnalyticsSendToEnum.FIREBASE,
          UseTrackingAnalyticsSendToEnum.FACEBOOK,
        ],
      });
    }
  }
  localStorage.removeItem(LocalStorageKeysEnum.USER_CULTURE_SELECTION_SOURCE);
};

export const getHomePageRedirectUrlPostLogin = async (
  userPrimaryLanguage: string,
) => {
  let dialect =
    process.env.NEXT_PUBLIC_DEFAULT_DIALECT_HOMEPAGE || DialectsEnum.HARYANVI;

  if (userPrimaryLanguage) {
    const availableDialects = await getAvailableDialects();
    const dialectObj = availableDialects.find(
      (_dialect) => _dialect.dialectAbbreviation === userPrimaryLanguage,
    );
    if (dialectObj) {
      dialect = `${dialectObj.urlValue}`;
    }
  }
  return `${dialect}`;
};

export const getCultureAbbreviationFromCulture = async (
  culture: string,
  dialect: string,
  lang: string,
) => {
  const availableCultures = await apiSdkInstance.setting.getCultures({
    dialect,
    lang,
  });
  const cultureAbbreviation = availableCultures.find(
    (_culture) => _culture.name.toLocaleLowerCase() === culture,
  )?.abbreviation;
  return cultureAbbreviation!;
};

export const setSourceValuesInSessionStorage = (
  contentId: number,
  sourcePage: SourcePagesEnum,
  sourceWidget: SourceWidgetsEnum | string,
) => {
  const _sourceWidget = Object.values(SourceWidgetsEnum).includes(
    sourceWidget as SourceWidgetsEnum,
  )
    ? sourceWidgetValues[sourceWidget as SourceWidgetsEnum]
    : sourceWidget;

  sessionStorage.setItem(
    `sourceValues_${contentId}`,
    JSON.stringify({
      sourcePage: sourcePagesValues[sourcePage],
      sourceWidget: _sourceWidget,
    }),
  );
};

export const getSourceValuesFromSessionStorage = (
  contentId: number,
): {
  sourcePage: SourcePagesEnum;
  sourceWidget: SourceWidgetsEnum;
} | null => {
  const sourceValues = sessionStorage.getItem(`sourceValues_${contentId}`);
  return sourceValues ? JSON.parse(sourceValues) : null;
};

export const updateUserClientId = async (forceApiCall = false) => {
  const currentAnalyticsId = await getUserClientId();
  if (!currentAnalyticsId) return;

  const analyticsIdFromLocalStorage = localStorage.getItem(
    LocalStorageKeysEnum.GA_CLIENT_ID,
  );

  if (analyticsIdFromLocalStorage !== currentAnalyticsId || forceApiCall) {
    apiSdkInstance.user.updateGAClientId(currentAnalyticsId);
    localStorage.setItem(LocalStorageKeysEnum.GA_CLIENT_ID, currentAnalyticsId);
  }
};

export const getUserClientId = async () => {
  const token = getClientSideCookieValue(cookiesValues[CookiesEnum.Token]);
  if (!token) return;

  // Dynamic import to avoid bundling Firebase in Edge Runtime (middleware)
  const { getAnalytics, getGoogleAnalyticsClientId } = await import(
    "firebase/analytics"
  );
  const { getFirebaseApp } = await import("./firebaseConfig");

  const analytics = getAnalytics(getFirebaseApp());
  if (!analytics) return;
  const currentAnalyticsId = await getGoogleAnalyticsClientId(analytics);

  return currentAnalyticsId;
};

export const getToken = async () => {
  if (isServer()) {
    return await getTokenAction();
  }
  return getClientSideCookieValue(cookiesValues[CookiesEnum.Token]) || "";
};

export const getDialect = async () => {
  if (isServer()) {
    const cachedParams = await getCachedParams();
    return cachedParams.dialect;
  }
  return (
    getClientSideCookieValue(cookiesValues[CookiesEnum.Dialect]) ||
    getDefaultDialect()
  );
};

export const scrollCurrentFocusedElementIntoView = (
  element: FocusableComponentLayout,
  block: ScrollLogicalPosition = "center",
  behavior: ScrollBehavior = "auto",
  checkEdges: boolean = false,
) => {
  const ua = getUACached();
  // browser.version is in the format of "120.0.5359.124"
  const browserMajorVersion = ua.browserVersion
    ? parseInt(ua.browserVersion.split(".")[0])
    : 0;

  const isChromeAbove108 =
    ua.browserName === "Chrome" && browserMajorVersion >= 108;

  // smooth scroll for chrome >108, where it can run smoothly
  const finalBehavior = isChromeAbove108 ? "smooth" : behavior;

  const elementNode = element.node;

  // Check if element is near edge before scrolling (only if checkEdges is enabled)
  let shouldCheckEdges = false;
  if (checkEdges) {
    const elementRect = elementNode.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const EDGE_THRESHOLD = 100; // pixels from edge to consider "near edge"

    // Check if element is near left or right edge
    shouldCheckEdges =
      elementRect.left < EDGE_THRESHOLD ||
      elementRect.right > viewportWidth - EDGE_THRESHOLD;
  }

  // First scroll into view
  elementNode.scrollIntoView({
    block,
    inline: "nearest",
    behavior: finalBehavior,
  });

  // Lightweight check: ensure both edges are visible after scroll
  // Only run if element was near edge before scrolling
  if (shouldCheckEdges) {
    requestAnimationFrame(() => {
      const elementRect = elementNode.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Find scrollable parent
      let parent = elementNode.parentElement;
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        if (
          (style.overflowX === "auto" || style.overflowX === "scroll") &&
          parent.scrollWidth > parent.clientWidth
        ) {
          const parentRect = parent.getBoundingClientRect();

          // Check right edge
          const rightOverflow = elementRect.right - parentRect.right;
          if (rightOverflow > 0) {
            parent.scrollBy({
              left: rightOverflow + 16, // Add padding
              behavior: finalBehavior,
            });
          }

          // Check left edge
          const leftOverflow = parentRect.left - elementRect.left;
          if (leftOverflow > 0) {
            parent.scrollBy({
              left: -(leftOverflow + 16), // Add padding
              behavior: finalBehavior,
            });
          }

          break;
        }
        parent = parent.parentElement;
      }
    });
  }
};

export const checkIsTruecallerLoginSupported = () => {
  const ua = getUACached();

  if (ua.osName === "iOS" || ua.deviceType !== "mobile") {
    return false;
  }

  return true;
};

export const getProtectedCDNBaseUrl = () => {
  return (
    process.env.NEXT_PUBLIC_PROTECTED_CDN_BASE_URL ||
    "https://d3e6qunn99eica.cloudfront.net"
  );
};

export const isMobileDevice = (userAgent?: string) => {
  if (userAgent) {
    const parser = new UAParser(userAgent);
    return parser.getDevice().type === "mobile";
  }
  return getUACached().deviceType === "mobile";
};

export const isTizenTV = () => {
  return getUACached().osName === "Tizen";
};

/**
 * Returns the client's public IP address using ipify and caches it in sessionStorage.
 * If called multiple times on the same page, reuses the in-flight request to avoid duplicates.
 * Runs only on the client; returns empty string on server or on failures.
 */
export async function getClientPublicIP(): Promise<string> {
  try {
    if (isServer()) return "";

    // Fast path: check in-memory cache first (avoids sessionStorage read)
    const w = window as unknown as {
      __cachedClientIP?: string;
      __ipifyPromise?: Promise<string>;
    };
    if (w.__cachedClientIP) return w.__cachedClientIP;

    const cached = sessionStorage.getItem(SessionStorageKeysEnum.CLIENT_IP);
    if (cached) {
      w.__cachedClientIP = cached;
      return cached;
    }

    // Reuse in-flight request if present to avoid duplicate fetches
    if (!w.__ipifyPromise) {
      w.__ipifyPromise = fetch("https://api.ipify.org?format=json", {
        cache: "no-store",
      })
        .then(async (resp) => {
          if (!resp.ok) return "";
          const data = await resp.json();
          const ip = (data?.ip as string) || "";
          if (ip) {
            sessionStorage.setItem(SessionStorageKeysEnum.CLIENT_IP, ip);
            w.__cachedClientIP = ip;
          }
          return ip;
        })
        .catch(() => "");
    }

    const ip = await w.__ipifyPromise;
    return ip || "";
  } catch {
    return "";
  }
}

// For LG webOS TVs, prevent responsive styles on versions below 1.0.5,
// because webOS internally scale font sizes, causing layout issues.
// This was resolved in app version 1.0.5 and above; for older versions, disable responsiveness.
export const shoudlPreventResponsivenessOnLowerLg = () => {
  if (!checkIsWebOS()) return false;

  const appVersion = window.appVersion;
  if (!appVersion) return false;
  const appVersionArray = appVersion.split(".").map(Number);

  if (!appVersionArray) return false;

  //[1,0,5]
  if (
    appVersionArray[0] == 1 &&
    appVersionArray[1] === 0 &&
    appVersionArray[2] < 5
  )
    return true;

  return false;
};

export const checkIsWebOS = (userAgent?: string) => {
  if (userAgent) {
    const uaParser = new UAParser(userAgent);
    return uaParser.getOS().name === "webOS";
  }
  return getUACached().osName === "webOS";
};

export const isTvBackButton = (
  event: React.KeyboardEvent<HTMLElement> | KeyboardEvent,
) => {
  return isTizenBackButton(event) || isWebOsBackButton(event);
};

export const isWebOsBackButton = (
  event: React.KeyboardEvent<HTMLElement> | KeyboardEvent,
) => {
  const isWebOS = checkIsWebOS();
  return isWebOS && event.key === "GoBack";
};

export const isTizenBackButton = (
  event: React.KeyboardEvent<HTMLElement> | KeyboardEvent,
) => {
  const isTizen = isTizenTV();
  return (
    isTizen &&
    (event.key === "XF86Back" ||
      event.code === "Escape" ||
      event.keyCode === 10009)
  );
};

export const shouldRedirectToSubscriptionFlow = () => {
  let redirectToSub = undefined;

  if (!isServer()) {
    redirectToSub =
      sessionStorage.getItem(SessionStorageKeysEnum.IS_TNLP_USER) === "false" ||
      sessionStorage.getItem(
        SessionStorageKeysEnum.IS_INDEPENDENCE_DAY_OFFER,
      ) === "true" ||
      sessionStorage.getItem(SessionStorageKeysEnum.IS_DIWALI_OFFER) === "true";
  }
  return redirectToSub;
};

export function getPaywallLink(
  isTrialEligibleUser: boolean,
  redirectToSub: boolean | undefined,
  lang?: string,
  dialect: string = DialectsEnum.HARYANVI,
  source: string = "home",
): string {
  dialect = dialect || DialectsEnum.HARYANVI;
  lang = lang || getDefaultLang();
  if (!isServer()) {
    const isIndependenceDayOffer =
      sessionStorage.getItem(
        SessionStorageKeysEnum.IS_INDEPENDENCE_DAY_OFFER,
      ) === "true";
    const isDiwaliOffer =
      sessionStorage.getItem(SessionStorageKeysEnum.IS_DIWALI_OFFER) === "true";
    if (isTrialEligibleUser && !redirectToSub) {
      return `/${lang}/${dialect}/paywall?source=${source}`;
    }
    if (redirectToSub) {
      if (isIndependenceDayOffer) {
        return `/${lang}/${dialect}/paywall?source=${source}&isIndependenceDayOffer=true`;
      } else if (isDiwaliOffer) {
        return `/${lang}/${dialect}/paywall?source=${source}&${URLQueryParamsEnum.isDiwaliOffer}=true`;
      } else if (
        sessionStorage.getItem(SessionStorageKeysEnum.IS_TNLP_USER) === "false"
      ) {
        return `/${lang}/${dialect}/paywall?isTNPLUser=false&source=${source}`;
      }
    }
  }
  return `/${lang}/${dialect}/paywall?source=${source}`;
}

export function buildCustomPaywallLink(
  source: string,
  isTrialEligibleUser: boolean,
  redirectToSub: boolean | undefined,
  couponCode?: string | null,
  overridingPlanId?: string | null,
  lang?: string,
  dialect?: string,
  contentId?: string,
  contentType?: string,
): string {
  let customPaywallLink = getPaywallLink(
    isTrialEligibleUser,
    redirectToSub,
    lang,
    dialect,
    source,
  );

  // Add coupon code and plan ID if they exist
  if (couponCode && overridingPlanId) {
    const separator = customPaywallLink.includes("?") ? "&" : "?";
    customPaywallLink += `${separator}couponCode=${couponCode}&planId=${overridingPlanId}`;
  }

  if (contentId && contentType) {
    const separator = customPaywallLink.includes("?") ? "&" : "?";
    customPaywallLink += `${separator}${URLQueryParamsEnum.CONTENT_ID}=${encodeURIComponent(contentId)}&${URLQueryParamsEnum.CONTENT_TYPE}=${encodeURIComponent(contentType)}`;
  }

  return customPaywallLink;
}

export const hasNextComingSoonEpisodeWithTeaser = (
  content: MappedShowDetail,
  currentEpisodeIndex: number,
) => {
  if (
    !content?.episodes ||
    currentEpisodeIndex + 1 >= content.episodes.length
  ) {
    return false;
  }

  const isNextEpisodeComingSoon =
    content &&
    content?.episodes?.[currentEpisodeIndex + 1]?.status ===
      ContentStatusEnum.COMING_SOON
      ? true
      : false;

  const isNextEpisdeTeaserAvailable = content?.episodes?.[
    currentEpisodeIndex + 1
  ]?.teaser
    ? true
    : false;

  return isNextEpisodeComingSoon && isNextEpisdeTeaserAvailable;
};

export async function createPhonepeMandates(
  targetApp: string,
  eventProperties: Record<string, string>,
  couponCode?: string,
  overridingPlanId?: string,
) {
  const data = await apiSdkInstance.payment.createPhonepeMandates({
    targetApp,
    eventProperties,
    couponCode,
    overridingPlanId,
  });
  return data;
}

export const getApiCacheKey = (url: string, lang: string, dialect: string) => {
  return `${url}_${lang}_${dialect}`;
};

export const getNextEpisodeUrl = (
  lang: LangEnum,
  dialect: string,
  contentType: string,
  showSlug: string,
  episodeNo: number,
  seasonId: number | null,
  contentDetail: MappedContentDetail | null,
): string | null => {
  if (!contentDetail || !("episodes" in contentDetail)) {
    return null;
  }

  const currentEpisodeIndex = episodeNo - 1;

  if (
    contentDetail.episodes[currentEpisodeIndex].status ===
    ContentStatusEnum.COMING_SOON
  ) {
    return null;
  }

  const hasNextEpisodeInCurrentSeason =
    episodeNo + 1 < (contentDetail?.episodes?.length || 0);

  if (hasNextEpisodeInCurrentSeason) {
    const nextEpisodeNo = episodeNo + 1;
    const nextEpisodeIndex = nextEpisodeNo - 1;

    if (
      contentDetail.episodes[nextEpisodeIndex].status ===
      ContentStatusEnum.COMING_SOON
    ) {
      const isNextEpisodeTeaserAvailable = hasNextComingSoonEpisodeWithTeaser(
        contentDetail,
        currentEpisodeIndex,
      );

      if (!isNextEpisodeTeaserAvailable) {
        return null;
      }
    }
    return generateWatchPageURL(
      lang as LangEnum,
      dialect as string,
      contentType as string,
      showSlug,
      nextEpisodeNo,
    );
  }

  const currentSeasonIndex = contentDetail?.seasonsList.findIndex(
    (season) => season.id === seasonId,
  );

  if (!currentSeasonIndex || currentSeasonIndex === -1) {
    return null;
  }

  const nextSeason = contentDetail?.seasonsList[currentSeasonIndex + 1];
  if (nextSeason) {
    return generateWatchPageURL(
      lang as LangEnum,
      dialect as string,
      contentType as string,
      showSlug,
      1,
      false,
      {
        seasonId: nextSeason.id.toString(),
      },
    );
  }

  return null;
};

export const handleBackButtonPress = (router: AppRouterInstance) => {
  if (window.isModalOpen && typeof window.closeModal === "function") {
    window.closeModal();

    return;
  }

  const dialect = getClientSideCookieValue(cookiesValues[CookiesEnum.Dialect]);
  const lang = getClientSideCookieValue(cookiesValues[CookiesEnum.Lang]);

  if (window.location.pathname === `/${lang}/${dialect}`) {
    closeApp();
    return;
  }

  router.back();
};

/**
 * This is used to avoid intersecting keypress events when the user is using the keyboard to navigate the page.
 * @see VideoPlayerProvider.tsx for implementation details.
 * @param eventName - The event to avoid intersecting (e.key)
 */
export const setAvoidIntersectingEvents = (
  eventName: AvoidIntersectingEventsEnum,
) => {
  window[`avoidIntersecting${eventName}Keydown`] = true;
};

export const removeAvoidIntersectingEvents = (
  eventName: AvoidIntersectingEventsEnum,
) => {
  delete window[`avoidIntersecting${eventName}Keydown`];
};

export const isAvoidIntersectingEvent = (eventName: string) => {
  return window[`avoidIntersecting${eventName}Keydown`] === true;
};

export const checkIsHLS265Supported = () => {
  // Check if MediaSource is available
  if (!window.MediaSource || !MediaSource.isTypeSupported) {
    return {
      every: false,
      some: false,
      oneByOne: [{ "all codecs are not supported": false }],
    };
  }

  // default list of codecs refer https://stagedotin.slack.com/archives/C06NYMGLNGH/p1756386502258019?thread_ts=1756375611.417539&cid=C06NYMGLNGH
  const h265Codecs = [
    'video/mp4; codecs="hvc1.1.6.L150.0,mp4a.40.2"',
    'video/mp4; codecs="hvc1.1.6.L120.0,mp4a.40.2"',
    'video/mp4; codecs="hvc1.1.6.L90.0,mp4a.40.2"',
    'video/mp4; codecs="hvc1.1.6.L63.0,mp4a.40.2"',
    'video/mp4; codecs="hev1.1.6.L150.0,mp4a.40.2"',
  ];

  // Check if any of the H.265 codecs are supported
  return {
    every: h265Codecs.every((codec) => MediaSource.isTypeSupported(codec)),
    some: h265Codecs.some((codec) => MediaSource.isTypeSupported(codec)),
    oneByOne: h265Codecs.map((codec) => ({
      [codec]: MediaSource.isTypeSupported(codec),
    })),
  };
};

export const setSourceUrlInLocalStorage = (url: string) => {
  localStorage.setItem(LocalStorageKeysEnum.SOURCE_CONTENT_BEFORE_PAYING, url);
};
export const clearSourceUrlInLocalStorage = () => {
  localStorage.removeItem(LocalStorageKeysEnum.SOURCE_CONTENT_BEFORE_PAYING);
};

export const getSourceUrlFromLocalStorage = (): string | null => {
  return localStorage.getItem(
    LocalStorageKeysEnum.SOURCE_CONTENT_BEFORE_PAYING,
  );
};

/**
 * Normalize a redirect path before storing as source URL.
 * It handles inputs that may or may not include language and/or dialect prefixes.
 * Examples handled:
 *  - "hi/rajasthani/movie/slug"
 *  - "/hi/rajasthani/movie/slug"
 *  - "/rajasthani/movie/slug"
 *  - "rajasthani/movie/slug"
 *  - "movie/slug"
 *  - "/movie/slug"
 *
 * Rules:
 *  - If the path already starts with a valid language (LangEnum), return it with a leading '/'.
 *  - If the path starts with a valid dialect (DialectsEnum) but no language, prefix with currentLanguage.
 *  - Otherwise prefix with both currentLanguage and currentDialect.
 */
export const normalizeRedirectForStorage = (
  redirect: string | null | undefined,
  currentLanguage: string,
  currentDialectUrlValue: string,
) => {
  if (!redirect) return `/${currentLanguage}/${currentDialectUrlValue}`;

  // remove leading slash for easier checks
  let path = redirect.startsWith("/") ? redirect.slice(1) : redirect;

  // remove any accidental duplicate leading slashes
  path = path.replace(/^\/+/, "");

  const segments = path.split("/").filter(Boolean);

  const langValues = Object.values(LangEnum);
  const dialectValues = Object.values(DialectsEnum);

  if (segments.length > 0 && langValues.includes(segments[0] as LangEnum)) {
    // already contains language (may also contain dialect)
    return `/${segments.join("/")}`;
  }

  if (
    segments.length > 0 &&
    dialectValues.includes(segments[0] as DialectsEnum)
  ) {
    // path contains dialect only, prefix language
    return `/${currentLanguage}/${segments.join("/")}`;
  }

  // no lang/dialect present, prefix both
  return `/${currentLanguage}/${currentDialectUrlValue}/${segments.join("/")}`;
};

export const extractFirstDigitFromString = (str: string) => {
  const match = str.match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
};

/**
 * Fetches UTM parameters from sessionStorage and returns a UTMDetailsInterface object.
 * Returns null if no UTM parameters are found.
 */
export const getUTMDetailsFromSessionStorage =
  (): UTMDetailsInterface | null => {
    if (typeof window === "undefined") {
      return null;
    }

    const utm_source = sessionStorage.getItem(EventPropertiesEnum.UTM_SOURCE);
    const utm_medium = sessionStorage.getItem(EventPropertiesEnum.UTM_MEDIUM);
    const utm_campaign = sessionStorage.getItem(
      EventPropertiesEnum.UTM_CAMPAIGN,
    );

    // Only create utmDetails if at least one UTM parameter exists
    if (utm_source || utm_medium || utm_campaign) {
      const utmDetails: UTMDetailsInterface = {};
      if (utm_source) utmDetails.utm_source = utm_source;
      if (utm_medium) utmDetails.utm_medium = utm_medium;
      if (utm_campaign) utmDetails.utm_campaign = utm_campaign;
      return utmDetails;
    }

    return null;
  };

/**
 * Gets UTM details and deeplink from session storage for marketing attribution.
 * Used during login flows and event tracking.
 */
export const getMarketingAttributionFromSessionStorage = (): {
  utmDetails: UTMDetailsInterface | null;
  deeplink: string | null;
} => {
  if (typeof window === "undefined") {
    return { utmDetails: null, deeplink: null };
  }

  const utmDetails = getUTMDetailsFromSessionStorage();
  const deeplink = sessionStorage.getItem(SessionStorageKeysEnum.DEEPLINK);

  return { utmDetails, deeplink };
};

export const closeApp = () => {
  // works only for LG App
  if (isTvEnv()) {
    window.parent.postMessage(
      {
        type: ParentPostMessageEnum.CLOSE_APP,
      },
      "*",
    );
  }
};

/**
 * This is used to parse a value from an enum object
 * @param enumObj - The enum object to parse
 * @param value - The value to parse
 * @returns The parsed value or null if the value is not in the enum
 */
export function parseEnum<T extends Record<string, string>>(
  enumObj: T,
  value: string | null | undefined,
): T[keyof T] | null {
  if (!value) return null;

  return (Object.values(enumObj) as string[]).includes(value)
    ? (value as T[keyof T])
    : null;
}

/**
 * Sets the redirectionAfterLoginTo URL parameter in the current URL.
 * This is used to redirect users to a specific page after login.
 *
 * @param redirectUrl - The URL to redirect to after login
 */
export const setRedirectionAfterLoginTo = (redirectUrl: string): void => {
  if (isServer()) return;

  const url = new URL(window.location.href);
  url.searchParams.set(
    URLQueryParamsEnum.REDIRECTION_AFTER_LOGIN_TO,
    redirectUrl,
  );
  window.history.replaceState({}, "", url.toString());
};

/**
 * Server-side helper to check if a content is special access content
 * @param cookieStore - The cookies() instance from next/headers
 * @param contentId - The content ID to check against special access content ID
 * @returns Object containing isSpecialAccessEnabled, specialAccessContentId, and isSpecialAccessContent
 */
export const checkSpecialAccessContent = (
  cookieStore: Awaited<ReturnType<typeof import("next/headers").cookies>>,
  contentId: string | number,
) => {
  const trialPlanId = cookieStore.get(
    cookiesValues[CookiesEnum.TrialPlanId],
  )?.value;
  const isSpecialAccessEnabled =
    trialPlanId === GamificationPlanIdEnum.SPECIAL_ACCESS;
  const specialAccessContentIds =
    decodeURIComponent(
      cookieStore.get(cookiesValues[CookiesEnum.SpecialAccessContentIds])
        ?.value || "",
    )?.split(",") || [];

  const isSpecialAccessContent =
    isSpecialAccessEnabled &&
    specialAccessContentIds &&
    specialAccessContentIds.includes(contentId.toString());

  return {
    isSpecialAccessEnabled,
    specialAccessContentIds,
    isSpecialAccessContent,
  };
};
